import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official'; // Or use @highcharts/react components
import 'highcharts/modules/exporting';

const formatNumber = (amount: number, locale: string = 'en-US', currency: string = 'USD'): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
};

interface ChartProps {
  title: string;
  height: number;
}

const BelowMinimumThreshold: React.FC<ChartProps> = ({ title, height = 345 }) => {
  const [sales, setSales] = React.useState([]);
  const chartComponentRef = React.useRef<HighchartsReact.RefObject>(null);
  React.useEffect(() => {
    const fetchData = async () => {
        try {
          const response = await fetch(`/dashboard/below-minimum-threshold/?entity_id=${localStorage.getItem('entity_id')}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data: any[] = await response.json();
          setSales(data['sales']);
        } catch (err) {
            console.log(err);     
        } finally {}
    };

    fetchData();

    const handleResize = () => {
      if (chartComponentRef.current && chartComponentRef.current.chart) {
        chartComponentRef.current.chart.reflow();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const options: Highcharts.Options = {
        chart: {
            type: 'scatter',
            zooming: {
                type: 'xy' as const
            }
        },
        lang: {
            thousandsSep: ','
        },
        title: {
            text: title,
            align: 'left',
            style: {
                fontFamily: 'Trebuchet MS'
            }
        },
        
        xAxis: {
            title: {
                text: 'Sales ($)',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            labels: {
                formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
                    const v = this.value as number;
                    const absv = Math.abs(v);
                    if (absv >= 1000000) return v / 1000000 + 'M';
                    if (absv >= 1000) return v / 1000 + 'K';
                    return String(v);
                },
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            startOnTick: true,
            endOnTick: true,
            showLastLabel: true,
            minRange: 1000,
            min: 0
        },
        yAxis: {
            title: {
                text: 'Gross Margin (%)',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            labels: {
                formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
                    return (this.value as number) + '%';
                },
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            startOnTick: true,
            endOnTick: true,
            showLastLabel: true
        },
        legend: {
            enabled: true
        },
        exporting: {
            enabled: true, // Enable the export menu
            buttons: {
                contextButton: {
                    menuItems: [
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                        'separator',
                        'printChart',
                    ],
                },
            },
        },

        plotOptions: {
            scatter: {
                marker: {
                    radius: 2.5,
                    symbol: 'circle',
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)'
                        }
                    }
                },
                pointInterval: 1000,
                states: {
                    hover: {
                        enabled: false
                    }
                },
                jitter: {
                    x: 0.005
                }
            }
        },
        tooltip: {
            stickOnContact: true,
            hideDelay: 99999999,
            useHTML: true,
            style: {
              pointerEvents: 'auto'
            },
            formatter: function(this: Highcharts.Point) {
                // Safely access id and series properties
                const id = (this as any).id ?? '';
                const name = (this as any).name ?? '';
                const x = (this as any).x ?? '';
                const y = (this as any).y ?? '';
                const z = (this as any).z ?? '';
                // For series, get the id or name if available
                let customerId = '';
                if (this.series && (this.series as any).userOptions && (this.series as any).userOptions.id) {
                    customerId = (this.series as any).userOptions.id;
                } else if (id) {
                    customerId = id;
                } else {
                    customerId = '';
                }
                return `<span style="font-family: Trebuchet MS;">Name: <b>${name}</b><br>Below Trigger Sales: ${formatNumber(x, 'en-US', 'USD')}<br>Below Trigger GM: ${formatNumber(z, 'en-US', 'USD')}<br>GPRate: ${y.toFixed(1)}%<br/><a href="/customers/${customerId}/?entity_id=${localStorage.getItem('entity_id')}">View Customer</a></span>`;
            }
            // pointFormat: 'Name: <b>{point.name}</b><br>Share of Stock Orders: {point.y}<br>Sales: ${point.x:.2f}<br/><a href="/customers/{point.id}/">View Customer</a>'
        },
        series: [
            {
                type: 'scatter',
                data: sales,
                showInLegend: false
            }
        ]
    };

    return (
        <div style={{ height: '100%', width: '100%' }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                allowChartUpdate={true}
                exportEnabled={true}
                ref={chartComponentRef}
                containerProps={{ style: { width: '100% !important', height: height, borderRadius: 5, boxShadow: 'none', margin: 0, padding: 0 } }}
            />
        </div>
    );
};

export default BelowMinimumThreshold;
